/**
 * Animación de letras al entrar en el viewport
 * Añade la clase 'animate-letters' a cualquier elemento de texto para activar la animación
 */

// ============================================
// CONFIGURACIÓN - Modifica estos valores según necesites
// ============================================
const CONFIG = {
  // Clase que activa la animación
  animationClass: 'animate-letters',
  
  // Distancia de translate desde arriba (en px)
  translateDistance: 100,
  
  // Duración del fade (opacidad) - en segundos
  fadeDuration: 0.6,
  
  // Duración del translate - en segundos
  translateDuration: 0.6,
  
  // Delay entre cada letra - en segundos
  letterDelay: 0.05,
  
  // Función de timing para la animación (ease, ease-in, ease-out, ease-in-out, linear)
  timingFunction: 'ease-out',
  
  // Opacidad inicial
  initialOpacity: 0,
  
  // Opacidad final
  finalOpacity: 1,
  
  // Margen para activar la animación antes de que el elemento esté completamente visible (en px)
  rootMargin: '0px 0px -100px 0px',
  
  // Umbral de visibilidad (0 = apenas visible, 1 = completamente visible)
  threshold: 0.1
};

// ============================================
// FUNCIONES
// ============================================

/**
 * Divide el texto en letras y las envuelve en spans
 * Cada palabra se envuelve en un span para evitar que se corten
 */
function wrapLetters(element) {
  const text = element.textContent;
  const words = text.split(' ');
  
  element.innerHTML = words.map((word, wordIndex) => {
    // Envolver cada letra de la palabra en un span
    const letters = word.split('').map((letter, letterIndex) => {
      // Preservar espacios y saltos de línea
      if (letter === ' ') {
        return '<span class="letter-space"> </span>';
      }
      return `<span class="letter" style="opacity: ${CONFIG.initialOpacity}; transform: translateY(-${CONFIG.translateDistance}px); display: inline-block;">${letter}</span>`;
    }).join('');
    
    // Envolver toda la palabra en un span adicional
    const wordWrapper = `<span class="word">${letters}</span>`;
    
    // Añadir espacio entre palabras (excepto después de la última palabra)
    return wordIndex < words.length - 1 ? wordWrapper + '<span class="letter-space"> </span>' : wordWrapper;
  }).join('');
}

/**
 * Anima las letras de un elemento
 */
function animateLetters(element) {
  const letters = element.querySelectorAll('.letter');
  
  letters.forEach((letter, index) => {
    const delay = index * CONFIG.letterDelay;
    
    letter.style.transition = `
      opacity ${CONFIG.fadeDuration}s ${CONFIG.timingFunction} ${delay}s,
      transform ${CONFIG.translateDuration}s ${CONFIG.timingFunction} ${delay}s
    `;
    
    // Usar requestAnimationFrame para asegurar que el navegador procese los estilos iniciales primero
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        letter.style.opacity = CONFIG.finalOpacity;
        letter.style.transform = 'translateY(0)';
      });
    });
  });
}

/**
 * Inicializa la animación para un elemento
 */
function initAnimation(element) {
  // Verificar si ya fue inicializado
  if (element.dataset.lettersInitialized === 'true') {
    return;
  }
  
  wrapLetters(element);
  element.dataset.lettersInitialized = 'true';
}

/**
 * Observa los elementos y activa la animación cuando entran en el viewport
 */
function observeElements() {
  const elements = document.querySelectorAll(`.${CONFIG.animationClass}`);
  
  if (elements.length === 0) {
    return;
  }
  
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const element = entry.target;
        
        // Inicializar si no se ha hecho
        if (element.dataset.lettersInitialized !== 'true') {
          initAnimation(element);
        }
        
        // Animar las letras
        animateLetters(element);
        
        // Dejar de observar este elemento después de animarlo
        observer.unobserve(element);
      }
    });
  }, {
    rootMargin: CONFIG.rootMargin,
    threshold: CONFIG.threshold
  });
  
  elements.forEach(element => {
    observer.observe(element);
  });
}

/**
 * Reinicia la animación de un elemento (útil si necesitas reanimar)
 */
function resetAnimation(element) {
  if (!element || !element.classList.contains(CONFIG.animationClass)) {
    return;
  }
  
  const letters = element.querySelectorAll('.letter');
  letters.forEach(letter => {
    letter.style.opacity = CONFIG.initialOpacity;
    letter.style.transform = `translateY(-${CONFIG.translateDistance}px)`;
    letter.style.transition = 'none';
  });
  
  // Forzar reflow
  element.offsetHeight;
  
  // Animar de nuevo
  animateLetters(element);
}

// ============================================
// INICIALIZACIÓN
// ============================================

// Inicializar cuando el DOM esté listo
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', observeElements);
} else {
  observeElements();
}

// Reobservar si se añaden nuevos elementos dinámicamente
const mutationObserver = new MutationObserver(() => {
  observeElements();
});

mutationObserver.observe(document.body, {
  childList: true,
  subtree: true
});

// Exportar funciones útiles al scope global (opcional)
window.TextLetterAnimation = {
  reset: resetAnimation,
  observe: observeElements,
  config: CONFIG
};